import { ChangeEvent, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { DCUIThemingPreviewContainer } from "@containers/common/dcui-theming-preview-container/dcui-theming-preview-container";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingCustomCssTabContainerProps = {
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    dcuiTheme: DCUITheme;
    inheritedDCUITheme: DCUITheme;
    pageLevel: PageLevelTypes;
    documentCaptureSteps: Array<DCUIThemingStep>;
    previewEntityType: PreviewEntityTypes;
    updateDCUIThemePropertyValue: (dcuiTheme: DCUITheme) => void;
};

export const DCUIThemingCustomCssTabContainer = (
    props: DCUIThemingCustomCssTabContainerProps
) => {
    const {
        isDisabled,
        dcuiContent,
        dcuiTheme,
        inheritedDCUITheme,
        pageLevel,
        documentCaptureSteps,
        previewEntityType,
        updateDCUIThemePropertyValue,
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const dcuiThemingResources = resources.pages.verificationServiceDetails.tabs.dcuiTheming;


    const handleCustomCssChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            cssStyle: newValue
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    const handleResetCustomCss = () => {
        const newPropertyValue: DCUITheme = {
            ...dcuiTheme,
            cssStyle: inheritedDCUITheme.cssStyle
        };
        updateDCUIThemePropertyValue(newPropertyValue);
    };

    return (
        <div>
            <ContentPageHeader title={dcuiThemingResources.subTabs.customCss.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer
                    description={dcuiThemingResources.subTabs.customCss.description}
                />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUITheme.cssStyle}
                            currentThemingPropertyValue={dcuiTheme.cssStyle}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={"document-capture-custom-css-text-input"}
                                automationId={"document-capture-custom-css-text-input"}
                                label={dcuiThemingResources.subTabs.customCss.inputs.customCss.label}
                                ariaLabel={dcuiThemingResources.subTabs.customCss.inputs.customCss.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleCustomCssChange}
                                value={dcuiTheme.cssStyle || ""}
                                disabled={isDisabled}
                                isRequired={false}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.customCss.inputs.customCss.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetCustomCss}
                            />
                        </ThemingFormGroupWrapper>
                    </ThemingFormContainer>

                    <DCUIThemingPreviewContainer
                        currentTab={DCUIThemingTabTypes.CUSTOM_CSS}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={previewEntityType}
                    />
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};
